import { getReadWorkspaceFileContentUrl } from '@/lib/api/generated/api';
import { getAccessToken, renewAccessToken } from '@/lib/auth/tokenStore';
import { env } from '@/lib/env';

/**
 * Constitution 2.2 exception, scoped exactly as plan.md's Complexity Tracking
 * table describes: the backend's content endpoint streams arbitrary bytes with
 * a dynamic `Content-Type` the OpenAPI client cannot describe, so a raw `fetch`
 * is the only way to read it with a bearer token attached. This mirrors
 * `useFileObjectUrl.ts` and `useFilePreviewContent.ts` — do not extend this
 * pattern beyond this one endpoint.
 *
 * A plain `<a href download>` pointed at this endpoint used to work, until
 * access tokens became mandatory on the API (2026-08-31): a browser anchor
 * cannot carry an `Authorization` header, so every such download 401'd. This
 * fetches the bytes the same authenticated way the preview hooks do, then
 * hands them to the browser's own download machinery via a blob URL.
 */
function send(url: string): Promise<Response> {
  const token = getAccessToken();
  return fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

export function useDownloadWorkspaceFile(workspaceId: string): {
  download: (path: string, name: string) => Promise<void>;
} {
  const download = async (path: string, name: string): Promise<void> => {
    const url = `${env.VITE_API_BASE_URL}${getReadWorkspaceFileContentUrl(workspaceId, { path })}`;

    let response = await send(url);
    // Renew once and replay, exactly as `client.ts` and the preview hooks do.
    if (response.status === 401 && getAccessToken()) {
      const renewed = await renewAccessToken();
      if (renewed) response = await send(url);
    }
    if (!response.ok) {
      throw new Error('This file could not be downloaded.');
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = name;
      anchor.click();
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  return { download };
}
